import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus } from "lucide-react";

interface AdminQuantityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  initialQuantity: number;
  onConfirm: (quantity: number) => void;
}

export function AdminQuantityDialog({
  isOpen,
  onClose,
  productName,
  initialQuantity,
  onConfirm,
}: AdminQuantityDialogProps) {
  const [quantity, setQuantity] = useState(Math.max(1, initialQuantity));
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (isOpen) {
      setQuantity(Math.max(1, initialQuantity));
      setEditing(false);
      setEditValue("");
    }
  }, [isOpen, initialQuantity]);

  const handleQuantityClick = () => {
    setEditValue(quantity.toString());
    setEditing(true);
  };

  const handleInputSave = () => {
    const num = parseInt(editValue) || 0;
    setQuantity(Math.max(0, num));
    setEditing(false);
    setEditValue("");
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInputSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditing(false);
      setEditValue("");
    }
  };

  const handleConfirm = () => {
    onConfirm(quantity);
    onClose();
  };

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      setTimeout(() => onClose(), 0);
    }
  };

  const buttonLabel = initialQuantity === 0 ? "Add to order" : "Update";

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent
        className="!w-[90vw] !max-w-sm mx-auto p-6"
        data-testid="admin-quantity-dialog"
        onPointerDownOutside={(e) => e.stopPropagation()}
        onInteractOutside={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle data-testid="admin-quantity-dialog-title">
            {productName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-6 py-4">
          <div className="flex items-center space-x-6">
            <Button
              onClick={() => setQuantity(q => Math.max(0, q - 1))}
              disabled={quantity <= 0}
              className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-4 min-w-[56px] h-14"
              data-testid="admin-qty-decrement"
            >
              <Minus className="h-5 w-5" />
            </Button>

            <div className="text-center min-w-[100px]">
              {editing ? (
                <Input
                  type="number"
                  inputMode="numeric"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={handleInputSave}
                  onKeyDown={handleInputKeyDown}
                  className="text-3xl font-bold text-center h-14 p-3 w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  autoFocus
                  data-testid="admin-qty-input"
                />
              ) : (
                <div
                  className="text-3xl font-bold text-google-blue cursor-pointer hover:bg-gray-100 rounded p-3 transition-colors"
                  onClick={handleQuantityClick}
                  data-testid="admin-qty-display"
                >
                  {quantity}
                </div>
              )}
            </div>

            <Button
              onClick={() => setQuantity(q => q + 1)}
              className="bg-[#1D3D88] hover:bg-[#1D3D88]/90 text-white rounded-lg p-4 min-w-[56px] h-14"
              data-testid="admin-qty-increment"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>

          <Button
            onClick={handleConfirm}
            className="w-full h-12 bg-cn-brand-green hover:bg-cn-brand-green-hover text-white text-base font-semibold"
            data-testid="admin-qty-confirm"
          >
            {buttonLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
